import React, { useEffect, useMemo, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import "./DateTimeRangePicker.css";

const DateTimeRangePicker = ({
  value,
  onChange,
  placeholder = "MM/DD/YYYY hh:mm aa - MM/DD/YYYY hh:mm aa",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState(value || { start: null, end: null });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectionStep, setSelectionStep] = useState("start");

  const [startTime, setStartTime] = useState({ hour: "12", minute: "00", period: "AM" });
  const [endTime, setEndTime] = useState({ hour: "01", minute: "00", period: "PM" });

  // input state
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");

  // ✅ NEW: hover preview date (used while choosing end date)
  const [hoveredDay, setHoveredDay] = useState(null);

  const hours = useMemo(
    () => Array.from({ length: 12 }, (_, i) => (i === 0 ? 12 : i).toString().padStart(2, "0")),
    []
  );
  const minutes = useMemo(
    () => Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, "0")),
    []
  );

  const formatDateTime = (date) => {
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    let h = date.getHours();
    const mins = date.getMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${month}/${day}/${year} ${h.toString().padStart(2, "0")}:${mins} ${ampm}`;
  };

  const formatDisplayValue = (range) => {
    if (!range?.start && !range?.end) return "";
    if (range.start && range.end) return `${formatDateTime(range.start)} – ${formatDateTime(range.end)}`;
    if (range.start) return `${formatDateTime(range.start)} – `;
    return "";
  };

  // Keep the input field in sync with external `value`
  useEffect(() => {
    setTempRange(value || { start: null, end: null });
    setInputValue(formatDisplayValue(value || { start: null, end: null }));
    setInputError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.start?.getTime?.(), value?.end?.getTime?.()]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i += 1) days.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) days.push(new Date(year, month, day));
    return days;
  };

  const createDateTime = (date, timeObj) => {
    const hour24 =
      timeObj.period === "AM"
        ? timeObj.hour === "12"
          ? 0
          : Number(timeObj.hour)
        : timeObj.hour === "12"
          ? 12
          : Number(timeObj.hour) + 12;

    const newDate = new Date(date);
    newDate.setHours(hour24, Number(timeObj.minute), 0, 0);
    return newDate;
  };

  const toTimeObj = (date) => {
    let h = date.getHours();
    const period = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    const hour = String(h).padStart(2, "0");
    const minute = String(date.getMinutes()).padStart(2, "0");
    return { hour, minute, period };
  };

  // parsing for typed input
  const parseDateTime = (s) => {
    const str = s.trim();
    const re = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
    const m = str.match(re);
    if (!m) return null;

    const month = Number(m[1]);
    const day = Number(m[2]);
    const year = Number(m[3]);
    let hour = Number(m[4]);
    const minute = Number(m[5]);
    const period = m[6].toUpperCase();

    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    if (hour < 1 || hour > 12) return null;
    if (minute < 0 || minute > 59) return null;

    if (period === "AM") hour = hour === 12 ? 0 : hour;
    else hour = hour === 12 ? 12 : hour + 12;

    const d = new Date(year, month - 1, day, hour, minute, 0, 0);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;

    return d;
  };

  const parseRange = (s) => {
    const raw = s.trim();
    if (!raw) return { start: null, end: null };

    const parts = raw.split(/\s*[–-]\s*/);
    const left = parts[0]?.trim();
    const right = parts.slice(1).join(" - ").trim();

    const start = left ? parseDateTime(left) : null;
    const end = right ? parseDateTime(right) : null;

    if (!start) return null;
    if (right && !end) return null;

    return { start, end: end || null };
  };

  const commitTypedValue = () => {
    const parsed = parseRange(inputValue);
    if (!parsed) {
      setInputError('Invalid format. Use: "MM/DD/YYYY hh:mm AM – MM/DD/YYYY hh:mm PM"');
      return;
    }

    setInputError("");
    setTempRange(parsed);

    if (parsed.start) setStartTime(toTimeObj(parsed.start));
    if (parsed.end) setEndTime(toTimeObj(parsed.end));

    if (parsed.start && parsed.end) {
      onChange?.(parsed);
      setSelectionStep("start");
      setIsOpen(false);
    } else {
      setSelectionStep("end");
    }
  };

  // date-only helpers
  const startOfDay = (d) => {
    if (!d) return null;
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  // preview end = real end if selected, otherwise hovered date (only when selecting end)
  const previewEnd =
    selectionStep === "end" && tempRange.start && !tempRange.end && hoveredDay
      ? createDateTime(hoveredDay, endTime)
      : tempRange.end;

  const previewStart = tempRange.start;

  const isInRange = (day, start, end) => {
    if (!day || !start || !end) return false;

    const d = startOfDay(day).getTime();
    const s = startOfDay(start).getTime();
    const e = startOfDay(end).getTime();

    const min = Math.min(s, e);
    const max = Math.max(s, e);

    return d > min && d < max;
  };

  const isSameDay = (d1, d2) => d1?.toDateString() === d2?.toDateString();

  const timeDisabled =
    (selectionStep === "start" && !tempRange.start) ||
    (selectionStep === "end" && !tempRange.end);

  const handleDateSelect = (date) => {
    if (selectionStep === "start") {
      const newDateTime = createDateTime(date, startTime);
      const next = { start: newDateTime, end: null };
      setTempRange(next);
      setInputValue(formatDisplayValue(next));
      setHoveredDay(null);
      return;
    }

    const newEndDateTime = createDateTime(date, endTime);

    let next;
    if (tempRange.start && newEndDateTime < tempRange.start) {
      next = { start: newEndDateTime, end: tempRange.start };
      setEndTime(startTime);
      setStartTime(endTime);
    } else {
      next = { ...tempRange, end: newEndDateTime };
    }

    setTempRange(next);
    setInputValue(formatDisplayValue(next));
    setHoveredDay(null);
  };

  // Double click = select FULL DAY (midnight -> next midnight) and APPLY
  const handleDayDoubleClick = (date) => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 1);

    const next = { start, end };
    setTempRange(next);
    setInputValue(formatDisplayValue(next));
    setInputError("");

    setStartTime({ hour: "12", minute: "00", period: "AM" });
    setEndTime({ hour: "12", minute: "00", period: "AM" });

    onChange?.(next);
    setIsOpen(false);
    setSelectionStep("start");
    setHoveredDay(null);
  };

  const handleTimeChange = (type, val) => {
    if (timeDisabled) return;

    if (selectionStep === "start") {
      const nextTime = { ...startTime, [type]: val };
      setStartTime(nextTime);

      if (tempRange.start) {
        const newStart = createDateTime(tempRange.start, nextTime);
        const next = { ...tempRange, start: newStart };
        setTempRange(next);
        setInputValue(formatDisplayValue(next));
      }
      return;
    }

    const nextTime = { ...endTime, [type]: val };
    setEndTime(nextTime);

    if (tempRange.end) {
      const newEnd = createDateTime(tempRange.end, nextTime);
      const next = { ...tempRange, end: newEnd };
      setTempRange(next);
      setInputValue(formatDisplayValue(next));
    }
  };

  const handlePrimary = () => {
    if (selectionStep === "start") {
      if (!tempRange.start) return;
      setSelectionStep("end");
      setHoveredDay(null);
      return;
    }
    if (!tempRange.end) return;

    onChange?.(tempRange);
    setIsOpen(false);
    setSelectionStep("start");
    setHoveredDay(null);
  };

  const handleCancel = () => {
    setTempRange(value || { start: null, end: null });
    setInputValue(formatDisplayValue(value || { start: null, end: null }));
    setInputError("");
    setSelectionStep("start");
    setIsOpen(false);
    setHoveredDay(null);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(direction === "prev" ? prev.getMonth() - 1 : prev.getMonth() + 1);
      return newMonth;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const currentTime = selectionStep === "start" ? startTime : endTime;

  const primaryLabel = selectionStep === "start" ? "SET START DATE" : "SET END DATE";
  const primaryDisabled = selectionStep === "start" ? !tempRange.start : !tempRange.end;

  return (
    <div className="datetime-range-picker">
      <TextField
        label="Date"
        variant="outlined"
        fullWidth
        value={inputValue}
        placeholder={placeholder}
        onChange={(e) => {
          setInputValue(e.target.value);
          setInputError("");
        }}
        onBlur={() => {
          if (!isOpen) commitTypedValue();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitTypedValue();
          }
        }}
        helperText={inputError || " "}
        error={Boolean(inputError)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  const next = !isOpen;
                  setIsOpen(next);
                  if (next) {
                    setSelectionStep("start");
                    setTempRange(value || { start: null, end: null });
                    setHoveredDay(null);
                  }
                }}
              >
                <CalendarMonthIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {isOpen && (
        <div className="picker-popup">
          <div className="picker-content picker-content--calendar-left">
            {/* CALENDAR (LEFT) */}
            <div className="calendar-section">
              <div className="month-nav">
                <button onClick={() => navigateMonth("prev")} className="nav-btn">‹</button>
                <h3 className="month-title">{monthYear}</h3>
                <button onClick={() => navigateMonth("next")} className="nav-btn">›</button>
              </div>

              <div className="calendar-grid">
                <div className="weekdays">
                  {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                    <div key={d} className="weekday">{d}</div>
                  ))}
                </div>

                <div className="days-grid">
                  {days.map((day, index) => (
                    <div key={index} className="day-cell">
                      {day && (
                        <button
                          onClick={() => handleDateSelect(day)}
                          onDoubleClick={() => handleDayDoubleClick(day)}
                          onMouseEnter={() => {
                            if (selectionStep === "end" && tempRange.start && !tempRange.end) {
                              setHoveredDay(day);
                            }
                          }}
                          onMouseLeave={() => {
                            if (selectionStep === "end" && tempRange.start && !tempRange.end) {
                              setHoveredDay(null);
                            }
                          }}
                          className={`day-btn
                            ${isSameDay(day, previewStart) ? "selected-start" : ""}
                            ${previewEnd && isSameDay(day, previewEnd) ? "selected-end" : ""}
                            ${previewStart && previewEnd && isInRange(day, previewStart, previewEnd) ? "in-range" : ""}
                            ${hoveredDay && isSameDay(day, hoveredDay) ? "hover-end" : ""}
                          `}
                        >
                          {day.getDate()}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* TIME (RIGHT) */}
            <div className={`time-section ${timeDisabled ? "disabled" : ""}`}>
              <div className="time-header">
                <p className="time-label">
                  {selectionStep === "start" ? "Set Start Time" : "Set End Time"}
                </p>
                {tempRange.start && selectionStep === "end" && (
                  <p className="start-time-display">
                    Start: {formatDateTime(tempRange.start)}
                  </p>
                )}
              </div>

              <div className="time-columns">
                <div className="time-column">
                  <div className="time-scroll">
                    {hours.map((hour) => (
                      <button
                        key={hour}
                        onClick={() => handleTimeChange("hour", hour)}
                        className={`time-btn ${currentTime.hour === hour ? "selected" : ""}`}
                        disabled={timeDisabled}
                      >
                        {hour}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="time-column">
                  <div className="time-scroll">
                    {minutes.map((minute) => (
                      <button
                        key={minute}
                        onClick={() => handleTimeChange("minute", minute)}
                        className={`time-btn ${currentTime.minute === minute ? "selected" : ""}`}
                        disabled={timeDisabled}
                      >
                        {minute}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="time-column">
                  <div className="period-buttons">
                    <button
                      onClick={() => handleTimeChange("period", "AM")}
                      className={`period-btn ${currentTime.period === "AM" ? "selected" : ""}`}
                      disabled={timeDisabled}
                    >
                      AM
                    </button>
                    <button
                      onClick={() => handleTimeChange("period", "PM")}
                      className={`period-btn ${currentTime.period === "PM" ? "selected" : ""}`}
                      disabled={timeDisabled}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="action-buttons">
            <div style={{ marginRight: "auto", display: "flex", gap: "10px" }}>
              <button
                onClick={() => {
                  const todayMidnight = new Date();
                  todayMidnight.setHours(0, 0, 0, 0);
                  const tomorrowMidnight = new Date(todayMidnight);
                  tomorrowMidnight.setDate(todayMidnight.getDate() + 1);

                  const next = { start: todayMidnight, end: tomorrowMidnight };
                  setTempRange(next);
                  setInputValue(formatDisplayValue(next));
                  setStartTime({ hour: "12", minute: "00", period: "AM" });
                  setEndTime({ hour: "12", minute: "00", period: "AM" });
                  setSelectionStep("start");
                  setCurrentMonth(new Date());
                  setInputError("");
                  onChange?.(next);
                  setHoveredDay(null);
                }}
                className="cancel-btn"
              >
                RESET
              </button>

              <button onClick={handleCancel} className="cancel-btn">
                CANCEL
              </button>
            </div>

            <button
              onClick={() => {
                handlePrimary();
                setInputValue(formatDisplayValue(tempRange));
              }}
              disabled={primaryDisabled}
              className="apply-btn"
            >
              {primaryLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimeRangePicker;
